import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { toMillis } from '~/lib/timestamps';
import { isPendingClientTaskWork } from '~/lib/taskClientResponse';
import { isProjectOperational } from '~/lib/projectLifecycle';
import type { Approval, Client, Invoice, Project, Task, UploadedFile } from '~/types/models';

type CommunicationEntry = {
    id: string;
    channel: 'app' | 'email' | 'whatsapp' | 'call' | 'note';
    title: string;
    direction: 'inbound' | 'outbound';
    actorName?: string;
    relativeAt: string;
    at: unknown;
};

export function useClientWorkspaceOverviewSignals(options: {
    client: MaybeRefOrGetter<Client | null>;
    communicationTimeline: MaybeRefOrGetter<CommunicationEntry[]>;
    threadSummary: MaybeRefOrGetter<{ count?: number; lastAt?: unknown } | null>;
    projects: MaybeRefOrGetter<Project[]>;
    tasks: MaybeRefOrGetter<Task[]>;
    files: MaybeRefOrGetter<UploadedFile[]>;
    approvals: MaybeRefOrGetter<Approval[]>;
    pendingTasks: MaybeRefOrGetter<Task[]>;
    pendingApprovals: MaybeRefOrGetter<Approval[]>;
    outstandingInvoices: MaybeRefOrGetter<Invoice[]>;
    notes: MaybeRefOrGetter<string>;
    hasHighlightedOnboarding: MaybeRefOrGetter<boolean>;
    needsAttention: () => boolean;
    nextMoveLabel: () => string;
    communicationChannelLabel: (channel: CommunicationEntry['channel']) => string;
}) {
    const operationalProjectIds = computed(() => new Set(
        toValue(options.projects)
            .filter(project => isProjectOperational(project.status))
            .map(project => project.id),
    ));
    const belongsToOperationalProject = (task: Task) => (
        !task.projectId || operationalProjectIds.value.has(task.projectId)
    );
    const lastContactedAt = computed<unknown>(() => {
        const client = toValue(options.client);
        const timeline = toValue(options.communicationTimeline);
        const thread = toValue(options.threadSummary);
        return [client?.lifecycle?.lastEngagedAt, timeline[0]?.at, thread?.lastAt]
            .filter(value => toMillis(value) > 0)
            .sort((left, right) => toMillis(right) - toMillis(left))[0] ?? null;
    });
    const daysSinceLastContact = computed<number | null>(() => {
        const lastAt = toMillis(lastContactedAt.value);
        return lastAt ? Math.max(0, Math.floor((Date.now() - lastAt) / 86_400_000)) : null;
    });
    const clientEntityLabel = computed(() => {
        const client = toValue(options.client);
        return String(client?.companyName || client?.businessName || '').trim() ? 'Organization' : 'Individual';
    });
    const projectStatsById = computed(() => Object.fromEntries(toValue(options.projects).map((project) => {
        const tasks = toValue(options.tasks).filter(task => task.projectId === project.id);
        const pendingApprovals = toValue(options.approvals).filter(approval => approval.projectId === project.id && approval.status === 'pending');
        const requestedInfoTasks = tasks.filter(task => task.clientVisible && isPendingClientTaskWork(task)).length;
        return [project.id, {
            taskTotal: tasks.length,
            taskCompleted: tasks.filter(task => ['done', 'completed'].includes(String(task.status || '').toLowerCase())).length,
            commentsCount: 0,
            filesCount: toValue(options.files).filter(file => file.entityType === 'project' && file.entityId === project.id).length,
            pendingApprovals: pendingApprovals.length,
            requestedInfoTasks,
            actionsRequired: requestedInfoTasks + pendingApprovals.length,
        }];
    })));
    const overviewConversationItems = computed(() => toValue(options.communicationTimeline).slice(0, 3));
    const waitingClientTasks = computed(() => toValue(options.pendingTasks).filter(task => belongsToOperationalProject(task) && task.clientVisible && isPendingClientTaskWork(task)));
    const overdueTasksCount = computed(() => toValue(options.pendingTasks).filter((task) => {
        if (!belongsToOperationalProject(task)) return false;
        const dueAt = toMillis(task.dueDate);
        return dueAt > 0 && dueAt < Date.now();
    }).length);
    const overdueInvoicesCount = computed(() => toValue(options.outstandingInvoices)
        .filter(invoice => String(invoice.status || '').toLowerCase() === 'overdue').length);
    const overviewNotesPreview = computed(() => {
        const text = String(toValue(options.notes) || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
        return text.length > 280 ? `${text.slice(0, 280).trim()}…` : text;
    });
    const overviewConversationSummary = computed(() => {
        const client = toValue(options.client);
        const latest = toValue(options.communicationTimeline)[0];
        if (!latest) {
            return client?.email || client?.phone || client?.contactNumber
                ? 'No active conversation yet. A follow-up from the comms workspace would restart the client thread and give the team a clearer signal to work from.'
                : 'No active conversation yet. Add a reliable contact route before the next follow-up so this client has a clear way to move forward.';
        }
        const latestSummary = latest.direction === 'inbound'
            ? `Latest client contact came in by ${options.communicationChannelLabel(latest.channel).toLowerCase()} ${latest.relativeAt}.`
            : `Latest outbound update went out ${latest.relativeAt} by ${latest.actorName || 'the team'}.`;
        const waitingTasks = waitingClientTasks.value.length;
        const pendingApprovals = toValue(options.pendingApprovals).length;
        const workSummary = waitingTasks > 0
            ? `${waitingTasks} task${waitingTasks === 1 ? '' : 's'} still need client input.`
            : pendingApprovals > 0
                ? `${pendingApprovals} approval${pendingApprovals === 1 ? '' : 's'} still need review.`
                : toValue(options.hasHighlightedOnboarding)
                    ? 'Onboarding is active and linked to the delivery flow.'
                    : 'Nothing urgent is blocked inside the conversation right now.';
        return `${latestSummary} ${workSummary}`;
    });
    const overviewHealthIndicators = computed(() => {
        const pendingTasks = toValue(options.pendingTasks);
        const pendingApprovals = toValue(options.pendingApprovals);
        const days = daysSinceLastContact.value;
        const health = options.needsAttention()
            ? { value: 'Needs attention', detail: options.nextMoveLabel() }
            : pendingTasks.length || pendingApprovals.length
                ? { value: 'In motion', detail: 'Work is active but not yet off track.' }
                : { value: 'Stable', detail: 'No urgent blockers are surfacing right now.' };
        const engagement = days == null
            ? { value: 'Unknown', detail: 'No contact rhythm has been logged yet.' }
            : days <= 3
                ? { value: 'Warm', detail: `Active contact within the last ${Math.max(1, days)} day${days === 1 ? '' : 's'}.` }
                : days <= 14
                    ? { value: 'Cooling', detail: `Last contact was ${days} days ago.` }
                    : { value: 'Stale', detail: `No contact for ${days} days.` };
        const cadence = toValue(options.threadSummary)?.count
            ? days != null && days <= 2
                ? { value: 'Fast', detail: 'Messages are still moving quickly.' }
                : days != null && days <= 7
                    ? { value: 'Steady', detail: 'The conversation is moving at a workable pace.' }
                    : { value: 'Quiet', detail: 'The thread may need a fresh nudge.' }
            : { value: 'Not started', detail: 'No thread is active in the workspace inbox yet.' };
        const riskScore = Number(waitingClientTasks.value.length > 0)
            + Number(pendingApprovals.length > 0)
            + Number(overdueTasksCount.value > 0)
            + Number(overdueInvoicesCount.value > 0)
            + Number((days || 0) >= 14);
        const risk = riskScore >= 3
            ? { value: 'High', detail: 'Multiple delivery or client signals need intervention.' }
            : riskScore >= 1
                ? { value: 'Medium', detail: 'There are early signs this client could drift.' }
                : { value: 'Low', detail: 'No major risk signals are visible right now.' };
        return [
            { label: 'Health', ...health },
            { label: 'Engagement', ...engagement },
            { label: 'Contact cadence', ...cadence },
            { label: 'Risk level', ...risk },
        ];
    });

    return {
        lastContactedAt,
        daysSinceLastContact,
        clientEntityLabel,
        projectStatsById,
        overviewConversationItems,
        waitingClientTasks,
        overdueTasksCount,
        overdueInvoicesCount,
        overviewNotesPreview,
        overviewConversationSummary,
        overviewHealthIndicators,
    };
}
